/*
 * Creatividades LG — grid con pestañas de filtro y "mostrar más".
 *
 * Todo el estado vive dentro de cada instancia de la sección: nunca se usan
 * selectores globales, de modo que dos grids en la misma página (o junto a un
 * case-success-grid-lg) no se interfieren.
 */
class CreativitiesGrid {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[data-creativities-grid]');
    if (!this.grid) return;

    this.cards = Array.from(this.grid.querySelectorAll('[data-creativities-card]'));
    this.tabs = Array.from(root.querySelectorAll('[data-creativities-tabs] .creativities__tab'));
    this.moreButton = root.querySelector('[data-creativities-more]');
    this.step = parseInt(root.dataset.itemsPerLoad, 10) || 12;
    this.filter = this.tabs.length ? this.tabs[0].dataset.filter : null;
    this.visibleCount = this.step;

    this.sortCards();
    this.bindEvents();
    this.render();
    this.setupThumbnailObserver();
  }

  /* La miniatura que genera Shopify al subir el vídeo es a veces un fotograma
     en negro (p.ej. si el vídeo empieza con un fundido). En vez de tocar cada
     vídeo a mano, se captura aquí el fotograma del segundo 1 en el propio
     navegador del visitante y se usa como miniatura. Solo se hace cuando la
     tarjeta entra en pantalla (nunca de golpe para todas) y con la copia más
     ligera del vídeo, para no añadir carga de red innecesaria; en conexiones
     lentas o con "ahorro de datos" activado, se omite del todo. */
  setupThumbnailObserver() {
    if (!('IntersectionObserver' in window) || this.shouldSkipThumbnails()) return;

    this.thumbObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this.thumbObserver.unobserve(entry.target);
        this.generateVideoThumb(entry.target);
      });
    }, { rootMargin: '200px' });

    this.cards.forEach((card) => {
      if (card.dataset.videoThumbSrc) this.thumbObserver.observe(card);
    });
  }

  shouldSkipThumbnails() {
    const connection = navigator.connection || navigator.webkitConnection || navigator.mozConnection;
    if (!connection) return false;
    return Boolean(connection.saveData) || /^(slow-2g|2g)$/.test(connection.effectiveType || '');
  }

  /* Muchos anuncios abren con una cortinilla de marca a color plano que dura
     más de un segundo: probar solo el segundo 1 deja esos casos igual de
     "vacíos" que antes. Se prueban varios instantes y se compara el
     contraste de cada fotograma (varianza de luminancia); en cuanto uno
     tiene contenido real se usa y se abandonan los siguientes intentos. */
  static VIDEO_THUMB_CANDIDATES = [1, 2.5, 4];
  static VIDEO_THUMB_VARIANCE_THRESHOLD = 18;

  generateVideoThumb(card) {
    const src = card.dataset.videoThumbSrc;
    if (!src) return;

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';

    const candidates = CreativitiesGrid.VIDEO_THUMB_CANDIDATES;
    let attempt = 0;
    let bestDataUrl = null;
    let bestVariance = -1;
    let settled = false;

    const cleanup = () => {
      settled = true;
      clearTimeout(timeoutId);
      video.removeAttribute('src');
      video.load();
    };
    /* Salvaguarda: si el vídeo no llega a cargar (red lenta, formato raro),
       se abandona sin dejar el elemento colgado a la espera para siempre. */
    const timeoutId = setTimeout(() => {
      if (bestDataUrl) this.applyThumb(card, bestDataUrl);
      cleanup();
    }, 10000);

    const frameVariance = () => {
      const sample = document.createElement('canvas');
      sample.width = 32;
      sample.height = 32;
      const ctx = sample.getContext('2d');
      ctx.drawImage(video, 0, 0, 32, 32);
      const { data } = ctx.getImageData(0, 0, 32, 32);
      let sum = 0;
      let sumSq = 0;
      const n = data.length / 4;
      for (let i = 0; i < data.length; i += 4) {
        const lum = (data[i] + data[i + 1] + data[i + 2]) / 3;
        sum += lum;
        sumSq += lum * lum;
      }
      const mean = sum / n;
      return Math.sqrt(Math.max(sumSq / n - mean * mean, 0));
    };

    const captureFrame = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      return canvas.toDataURL('image/jpeg', 0.8);
    };

    const seekNext = () => {
      if (settled) return;
      if (attempt >= candidates.length) {
        if (bestDataUrl) this.applyThumb(card, bestDataUrl);
        cleanup();
        return;
      }
      const target = Math.min(candidates[attempt], Math.max(video.duration - 0.1, 0));
      attempt += 1;
      video.currentTime = target;
    };

    video.addEventListener('loadedmetadata', seekNext, { once: true });

    video.addEventListener('seeked', () => {
      if (settled) return;
      try {
        const variance = frameVariance();
        if (variance > bestVariance) {
          bestVariance = variance;
          bestDataUrl = captureFrame();
        }
        if (variance >= CreativitiesGrid.VIDEO_THUMB_VARIANCE_THRESHOLD) {
          this.applyThumb(card, bestDataUrl);
          cleanup();
        } else {
          seekNext();
        }
      } catch (err) {
        /* Canvas contaminado (CORS) u otro fallo: se conserva la miniatura original. */
        cleanup();
      }
    });

    video.addEventListener('error', cleanup, { once: true });

    video.src = src;
  }

  applyThumb(card, dataUrl) {
    const media = card.querySelector('.creativities__media');
    if (!media) return;

    let img = media.querySelector('[data-creativities-thumb]');
    if (!img) {
      img = document.createElement('img');
      img.dataset.creativitiesThumb = '';
      img.className = 'creativities__image';
      img.alt = card.getAttribute('aria-label') || '';
      media.insertBefore(img, media.firstChild);
    }
    /* Si el <img> original trae "srcset" (miniatura responsive de Shopify),
       el navegador lo prioriza sobre "src" y seguiría mostrando la miniatura
       vieja aunque "src" ya apunte al fotograma capturado. */
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = dataUrl;
  }

  /* El campo "orden" del metaobjeto no se puede ordenar desde Liquid (devuelve
     un objeto, no un escalar), así que se ordena aquí. Array.sort es estable:
     si no hay orden definido, se respeta el que devuelve Shopify. */
  sortCards() {
    const hasOrder = this.cards.some((card) => parseInt(card.dataset.orden, 10) > 0);
    if (!hasOrder) return;

    const sorted = this.cards.slice().sort((a, b) => {
      const orderA = parseInt(a.dataset.orden, 10) || Number.MAX_SAFE_INTEGER;
      const orderB = parseInt(b.dataset.orden, 10) || Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    const fragment = document.createDocumentFragment();
    sorted.forEach((card) => fragment.appendChild(card));
    this.grid.appendChild(fragment);
    this.cards = sorted;
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.setFilter(tab));
    });

    if (this.moreButton) {
      this.moreButton.addEventListener('click', () => {
        this.visibleCount += this.step;
        this.render();
      });
    }

    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.openModal(card));
    });
  }

  setFilter(tab) {
    this.tabs.forEach((item) => {
      const isActive = item === tab;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.filter = tab.dataset.filter;
    this.visibleCount = this.step;
    this.render();
  }

  matchingCards() {
    if (!this.filter) return this.cards;
    return this.cards.filter((card) => card.dataset.categoria === this.filter);
  }

  render() {
    const matching = this.matchingCards();

    this.cards.forEach((card) => {
      card.hidden = true;
    });

    matching.slice(0, this.visibleCount).forEach((card) => {
      card.hidden = false;
    });

    if (this.moreButton) {
      this.moreButton.hidden = matching.length <= this.visibleCount;
    }

    this.root.classList.toggle('creativities--empty', matching.length === 0);
  }

  /* El custom element modal-dialog se mueve a <body> en su connectedCallback,
     así que se busca por id y no como descendiente de la sección. */
  modal() {
    return document.getElementById(`CreativitiesModal-${this.root.dataset.sectionId}`);
  }

  openModal(card) {
    const modal = this.modal();
    const template = card.querySelector('[data-creativities-full]');
    if (!modal || !template || typeof modal.show !== 'function') return;

    const target = modal.querySelector('[data-creativities-modal-media]');
    if (!target) return;

    target.replaceChildren(template.content.cloneNode(true));
    this.watchModal(modal, target);
    modal.show(card);

    /* Un <video> clonado desde un <template> no arranca la carga de sus
       <source> por sí solo: hay que pedirle load() antes de reproducir. */
    const video = target.querySelector('video');
    if (video) {
      video.load();
      const playPromise = video.play();
      if (playPromise !== undefined) playPromise.catch(() => {});
    }
  }

  /* modal-dialog no emite ningún evento al cerrarse, así que se vigila el
     atributo "open" para vaciar el contenido (y con él detener el vídeo). */
  watchModal(modal, target) {
    if (this.modalObserver) return;

    this.modalObserver = new MutationObserver(() => {
      if (!modal.hasAttribute('open')) target.replaceChildren();
    });
    this.modalObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
  }
}

function initCreativitiesGrids(scope = document) {
  scope.querySelectorAll('[data-creativities]').forEach((root) => {
    if (root.dataset.creativitiesReady === 'true') return;
    root.dataset.creativitiesReady = 'true';
    new CreativitiesGrid(root);
  });
}

initCreativitiesGrids();

document.addEventListener('shopify:section:load', (event) => {
  initCreativitiesGrids(event.target);
});
